import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Save,
  Plus,
  Crown,
  Users,
  Dumbbell,
  BarChart3,
  Loader2,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  X,
  CreditCard,
  Activity,
  History,
  Receipt,
  ClipboardList,
  Trash2,
  Edit,
  Heart,
  Star,
  MessageSquare,
  Target,
  Smile,
  FileQuestion,
  Layers,
  Check,
  FileText,
  Database,
  Calendar,
} from "lucide-react";
import { API } from "@/App";
import { formatDateTimeTR } from "@/utils/dateUtils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

export default function AdminSettingsModule() {
  const { t, i18n } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "events", "admin", "surveys", "reports", "evaluations"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType, getLocalizedMeasurement, getLocalizedExpenseType, refreshData } = useLocalizedData();

  // Local helper to get localized name from item with translations
  const getLocalName = (item) => {
    if (!item) return "";
    const name = typeof item === 'string' ? item : item.name;
    if (!name) return "";
    const lang = (i18n.language || "tr").toLowerCase().split("-")[0];
    if (lang === "tr") return name;
    if (typeof item === 'object' && item.translations && item.translations[lang]) {
      return item.translations[lang];
    }
    return getLocalizedBranch(name) !== name ? getLocalizedBranch(name) : name;
  };
  const getLocalTrainingType = (item) => {
    if (!item) return "";
    const name = typeof item === 'string' ? item : item.name;
    if (!name) return "";
    const lang = (i18n.language || "tr").toLowerCase().split("-")[0];
    if (lang === "tr") return name;
    if (typeof item === 'object' && item.translations && item.translations[lang]) {
      return item.translations[lang];
    }
    return getLocalizedTrainingType(name) !== name ? getLocalizedTrainingType(name) : name;
  };
  const getLocalExpenseType = (item) => {
    if (!item) return "";
    const name = typeof item === 'string' ? item : item.name;
    if (!name) return "";
    const lang = (i18n.language || "tr").toLowerCase().split("-")[0];
    if (lang === "tr") return name;
    if (typeof item === 'object' && item.translations && item.translations[lang]) {
      return item.translations[lang];
    }
    return getLocalizedExpenseType(name) !== name ? getLocalizedExpenseType(name) : name;
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Plan editing state
  const [plans, setPlans] = useState({});
  const [branches, setBranches] = useState([]);
  const [trainingTypes, setTrainingTypes] = useState([]); // [{name: "Birebir", max_participants: 1}, ...]
  const [memberCards, setMemberCards] = useState([]);
  const [measurementParams, setMeasurementParams] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState(["tr", "en"]);
  const [selectedTransLang, setSelectedTransLang] = useState("en");
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [newTrainingTypeMaxParticipants, setNewTrainingTypeMaxParticipants] = useState(1);
  
  // Survey types state
  const [surveyTypes, setSurveyTypes] = useState([]);
  const [showAddSurveyTypeModal, setShowAddSurveyTypeModal] = useState(false);
  const [editingSurveyType, setEditingSurveyType] = useState(null);
  const [surveyTypeForm, setSurveyTypeForm] = useState({
    name: "",
    description: "",
    icon: "ClipboardList",
    color: "blue"
  });
  
  // Survey questions editing state
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [selectedSurveyTypeForQuestions, setSelectedSurveyTypeForQuestions] = useState(null);
  const [surveyTypeQuestions, setSurveyTypeQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    text: "",
    type: "text",
    options: [],
    is_required: true
  });
  const [newOption, setNewOption] = useState("");
  
  // Modal states
  const [newBranch, setNewBranch] = useState("");
  const [showAddBranchModal, setShowAddBranchModal] = useState(false);
  const [newTrainingType, setNewTrainingType] = useState("");
  const [showAddTrainingTypeModal, setShowAddTrainingTypeModal] = useState(false);
  const [newCardName, setNewCardName] = useState("");
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [newMeasurementParam, setNewMeasurementParam] = useState({ name: "", unit: "" });
  const [showAddMeasurementModal, setShowAddMeasurementModal] = useState(false);
  const [newExpenseType, setNewExpenseType] = useState("");
  const [showAddExpenseTypeModal, setShowAddExpenseTypeModal] = useState(false);
  
  // New Plan Creation state
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [newPlanForm, setNewPlanForm] = useState({
    name: "",
    description: "",
    monthly_price: 0,
    yearly_price: 0,
    allowed_modules: [],
    limits: { members: -1 }
  });
  const [globalYearlyDiscount, setGlobalYearlyDiscount] = useState(17); // Global yıllık indirim oranı
  const [systemModules, setSystemModules] = useState([]);
  const [showModulesModal, setShowModulesModal] = useState(false);
  const [selectedPlanForModules, setSelectedPlanForModules] = useState(null);
  const [deletePlanDialog, setDeletePlanDialog] = useState({ open: false, planId: null, planName: "" });
  const [deletingPlan, setDeletingPlan] = useState(false);
  
  // Plan features modal state
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [selectedPlanForFeatures, setSelectedPlanForFeatures] = useState(null);
  const [newFeatureText, setNewFeatureText] = useState("");
  
  // Revenue report state
  const [revenueReport, setRevenueReport] = useState(null);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [revenuePeriod, setRevenuePeriod] = useState("all");
  
  // Form types state (similar to survey types)
  const [formTypes, setFormTypes] = useState([]);
  const [showAddFormTypeModal, setShowAddFormTypeModal] = useState(false);
  const [editingFormType, setEditingFormType] = useState(null);
  const [formTypeForm, setFormTypeForm] = useState({
    name: "",
    description: "",
    icon: "FileText",
    color: "orange"
  });
  
  // Form questions editing state
  const [showFormQuestionsModal, setShowFormQuestionsModal] = useState(false);
  const [selectedFormTypeForQuestions, setSelectedFormTypeForQuestions] = useState(null);
  const [formTypeQuestions, setFormTypeQuestions] = useState([]);
  const [loadingFormQuestions, setLoadingFormQuestions] = useState(false);
  const [showAddFormQuestionModal, setShowAddFormQuestionModal] = useState(false);
  const [editingFormQuestion, setEditingFormQuestion] = useState(null);
  const [formQuestionForm, setFormQuestionForm] = useState({
    text: "",
    type: "text",
    options: [],
    is_required: true
  });
  const [newFormOption, setNewFormOption] = useState("");
  
  // Delete confirmation state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'survey'|'form', id, name }

  useEffect(() => {
    // Small delay to ensure auth token is ready
    const timer = setTimeout(() => {
      fetchSettings();
      fetchStats();
      fetchPriceHistory();
      fetchSurveyTypes();
      fetchFormTypes();
      fetchSystemModules();
      fetchRevenueReport();
      // initializeDefaultPlans(); // Disabled - don't reset plans on every load
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Survey type icons and colors
  const SURVEY_ICONS = [
    { name: "ClipboardList", icon: ClipboardList, label: "Anket" },
    { name: "Heart", icon: Heart, label: "Sağlık" },
    { name: "Star", icon: Star, label: "Değerlendirme" },
    { name: "MessageSquare", icon: MessageSquare, label: "Geri Bildirim" },
    { name: "Activity", icon: Activity, label: "Aktivite" },
    { name: "Users", icon: Users, label: "Sosyal" },
    { name: "Target", icon: Target, label: "Hedef" },
    { name: "Smile", icon: Smile, label: "Motivasyon" },
    { name: "FileText", icon: FileText, label: "Form" },
    { name: "FileQuestion", icon: FileQuestion, label: "Soru" },
  ];
  
  const SURVEY_COLORS = [
    { name: "blue", label: "Mavi", class: "bg-blue-100 text-blue-700" },
    { name: "green", label: "Yeşil", class: "bg-green-100 text-green-700" },
    { name: "purple", label: "Mor", class: "bg-purple-100 text-purple-700" },
    { name: "orange", label: "Turuncu", class: "bg-orange-100 text-orange-700" },
    { name: "red", label: "Kırmızı", class: "bg-red-100 text-red-700" },
    { name: "amber", label: "Kehribar", class: "bg-amber-100 text-amber-700" },
  ];

  const getIconComponent = (iconName) => {
    const found = SURVEY_ICONS.find((i) => i.name === iconName);
    return found ? found.icon : ClipboardList;
  };

  const getColorClass = (colorName) => {
    const found = SURVEY_COLORS.find((c) => c.name === colorName);
    return found ? found.class : "bg-blue-100 text-blue-700";
  };

  // Fetch survey types
  const fetchSurveyTypes = async () => {
    try {
      const response = await authFetch(`${API}/surveys/types?include_inactive=true`);
      if (response.ok) {
        const data = await response.json();
        setSurveyTypes(data);
      }
    } catch (error) {
      console.error("Error fetching survey types:", error);
    }
  };

  // Save survey type
  const handleSaveSurveyType = async () => {
    if (!surveyTypeForm.name.trim()) {
      toast.error(t("surveys:surveyTypeNameRequired"));
      return;
    }

    try {
      const url = editingSurveyType
        ? `${API}/surveys/types/${editingSurveyType.type_id}`
        : `${API}/surveys/types`;

      const response = await authFetch(url, {
        method: editingSurveyType ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(surveyTypeForm),
      });

      if (response.ok) {
        toast.success(editingSurveyType ? t("surveys:surveyTypeUpdated") : t("surveys:surveyTypeCreated"));
        fetchSurveyTypes();
        handleCloseSurveyTypeModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  // Delete survey type
  const handleDeleteSurveyType = async (typeId, typeName = "") => {
    setDeleteTarget({ type: 'survey', id: typeId, name: typeName });
    setShowDeleteDialog(true);
  };

  // Confirm delete action
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      let url = "";
      let successMsg = "";
      let fetchFn = null;
      
      if (deleteTarget.type === 'survey') {
        url = `${API}/surveys/types/${deleteTarget.id}`;
        successMsg = t("surveys:surveyTypeDeleted");
        fetchFn = fetchSurveyTypes;
      } else if (deleteTarget.type === 'form') {
        url = `${API}/member-forms/types/${deleteTarget.id}`;
        successMsg = t("surveys:formTypeDeleted");
        fetchFn = fetchFormTypes;
      } else if (deleteTarget.type === 'survey-question') {
        url = `${API}/surveys/types/${deleteTarget.parentId}/questions/${deleteTarget.id}`;
        successMsg = "Soru silindi";
        fetchFn = () => fetchSurveyTypeQuestions(deleteTarget.parentId);
      } else if (deleteTarget.type === 'form-question') {
        url = `${API}/member-forms/types/${deleteTarget.parentId}/questions/${deleteTarget.id}`;
        successMsg = "Soru silindi";
        fetchFn = () => fetchFormTypeQuestions(deleteTarget.parentId);
      }
      
      const response = await authFetch(url, { method: "DELETE" });

      if (response.ok) {
        toast.success(successMsg);
        if (fetchFn) fetchFn();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:cannotDeleteInUse"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  // Open survey type modal
  const handleOpenSurveyTypeModal = (type = null) => {
    if (type) {
      setEditingSurveyType(type);
      setSurveyTypeForm({
        name: type.name,
        description: type.description || "",
        icon: type.icon || "ClipboardList",
        color: type.color || "blue",
      });
    } else {
      setEditingSurveyType(null);
      setSurveyTypeForm({
        name: "",
        description: "",
        icon: "ClipboardList",
        color: "blue",
      });
    }
    setShowAddSurveyTypeModal(true);
  };

  // Close survey type modal
  const handleCloseSurveyTypeModal = () => {
    setShowAddSurveyTypeModal(false);
    setEditingSurveyType(null);
    setSurveyTypeForm({
      name: "",
      description: "",
      icon: "ClipboardList",
      color: "blue",
    });
  };
  
  // ========== SURVEY QUESTIONS MANAGEMENT ==========
  
  // Fetch questions for a survey type
  const fetchSurveyTypeQuestions = async (typeId) => {
    setLoadingQuestions(true);
    try {
      const response = await authFetch(`${API}/surveys/types/${typeId}`);
      if (response.ok) {
        const data = await response.json();
        setSurveyTypeQuestions(data.questions || []);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error(t("surveys:questionsLoadFailed"));
    } finally {
      setLoadingQuestions(false);
    }
  };
  
  // Open questions modal
  const handleOpenQuestionsModal = async (surveyType) => {
    setSelectedSurveyTypeForQuestions(surveyType);
    setShowQuestionsModal(true);
    await fetchSurveyTypeQuestions(surveyType.type_id);
  };
  
  // Close questions modal
  const handleCloseQuestionsModal = () => {
    setShowQuestionsModal(false);
    setSelectedSurveyTypeForQuestions(null);
    setSurveyTypeQuestions([]);
  };
  
  // Open add/edit question modal
  const handleOpenQuestionModal = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      setQuestionForm({
        text: question.text,
        type: question.type,
        options: question.options || [],
        is_required: question.is_required ?? true
      });
    } else {
      setEditingQuestion(null);
      setQuestionForm({
        text: "",
        type: "text",
        options: [],
        is_required: true
      });
    }
    setNewOption("");
    setShowAddQuestionModal(true);
  };
  
  // Close question modal
  const handleCloseQuestionModal = () => {
    setShowAddQuestionModal(false);
    setEditingQuestion(null);
    setQuestionForm({
      text: "",
      type: "text",
      options: [],
      is_required: true
    });
    setNewOption("");
  };
  
  // Add option to multiple choice
  const handleAddOption = () => {
    if (newOption.trim() && !questionForm.options.includes(newOption.trim())) {
      setQuestionForm({
        ...questionForm,
        options: [...questionForm.options, newOption.trim()]
      });
      setNewOption("");
    }
  };
  
  // Remove option from multiple choice
  const handleRemoveOption = (optionToRemove) => {
    setQuestionForm({
      ...questionForm,
      options: questionForm.options.filter(opt => opt !== optionToRemove)
    });
  };
  
  // Save question (add or update)
  const handleSaveQuestion = async () => {
    if (!questionForm.text.trim()) {
      toast.error(t('common:questionTextRequired'));
      return;
    }
    
    if (questionForm.type === "multiple_choice" && questionForm.options.length < 2) {
      toast.error(t("surveys:minTwoOptionsRequired"));
      return;
    }
    
    const typeId = selectedSurveyTypeForQuestions.type_id;
    
    try {
      const url = editingQuestion
        ? `${API}/surveys/types/${typeId}/questions/${editingQuestion.question_id}`
        : `${API}/surveys/types/${typeId}/questions`;
      
      const response = await authFetch(url, {
        method: editingQuestion ? "PUT" : "POST",
        body: JSON.stringify(questionForm),
      });
      
      if (response.ok) {
        toast.success(editingQuestion ? t("surveys:questionUpdated") : t("surveys:questionAdded"));
        await fetchSurveyTypeQuestions(typeId);
        handleCloseQuestionModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  // Delete question
  const handleDeleteQuestion = async (questionId, questionText = "") => {
    const typeId = selectedSurveyTypeForQuestions.type_id;
    setDeleteTarget({ 
      type: 'survey-question', 
      id: questionId, 
      parentId: typeId,
      name: questionText 
    });
    setShowDeleteDialog(true);
  };
  
  // Question type labels
  const QUESTION_TYPES = [
    { value: "text", label: "Metin Girişi" },
    { value: "yes_no", label: "Evet / Hayır" },
    { value: "boolean", label: "Evet / Hayır" },
    { value: "rating", label: "Derecelendirme (1-5)" },
    { value: "emoji", label: "Emoji Seçimi" },
    { value: "multiple_choice", label: "Çoktan Seçmeli" },
    { value: "select", label: "Tekli Seçim" },
    { value: "multiselect", label: "Çoklu Seçim" }
  ];
  
  const getQuestionTypeLabel = (type) => {
    const found = QUESTION_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };
  
  // ========== FORM TYPES MANAGEMENT (Admin) ==========
  
  // Fetch form types
  const fetchFormTypes = async () => {
    try {
      const response = await authFetch(`${API}/member-forms/types?include_inactive=true`);
      if (response.ok) {
        const data = await response.json();
        setFormTypes(data);
      }
    } catch (error) {
      console.error("Error fetching form types:", error);
    }
  };

  // Save form type
  const handleSaveFormType = async () => {
    if (!formTypeForm.name.trim()) {
      toast.error(t("surveys:toast_formTuruAdiGerekli"));
      return;
    }

    try {
      const url = editingFormType
        ? `${API}/member-forms/types/${editingFormType.type_id}`
        : `${API}/member-forms/types`;

      const response = await authFetch(url, {
        method: editingFormType ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formTypeForm),
      });

      if (response.ok) {
        toast.success(editingFormType ? t("surveys:formTypeUpdated") : t("surveys:formTypeCreated"));
        fetchFormTypes();
        handleCloseFormTypeModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  // Delete form type
  const handleDeleteFormType = async (typeId, typeName = "") => {
    setDeleteTarget({ type: 'form', id: typeId, name: typeName });
    setShowDeleteDialog(true);
  };

  // Open form type modal
  const handleOpenFormTypeModal = (type = null) => {
    if (type) {
      setEditingFormType(type);
      setFormTypeForm({
        name: type.name,
        description: type.description || "",
        icon: type.icon || "FileText",
        color: type.color || "orange",
      });
    } else {
      setEditingFormType(null);
      setFormTypeForm({
        name: "",
        description: "",
        icon: "FileText",
        color: "orange",
      });
    }
    setShowAddFormTypeModal(true);
  };

  // Close form type modal
  const handleCloseFormTypeModal = () => {
    setShowAddFormTypeModal(false);
    setEditingFormType(null);
    setFormTypeForm({
      name: "",
      description: "",
      icon: "FileText",
      color: "orange",
    });
  };
  
  // Fetch questions for a form type
  const fetchFormTypeQuestions = async (typeId) => {
    setLoadingFormQuestions(true);
    try {
      const response = await authFetch(`${API}/member-forms/types/${typeId}`);
      if (response.ok) {
        const data = await response.json();
        setFormTypeQuestions(data.questions || []);
      }
    } catch (error) {
      console.error("Error fetching form questions:", error);
      toast.error(t("surveys:questionsLoadFailed"));
    } finally {
      setLoadingFormQuestions(false);
    }
  };
  
  // Open form questions modal
  const handleOpenFormQuestionsModal = async (formType) => {
    setSelectedFormTypeForQuestions(formType);
    setShowFormQuestionsModal(true);
    await fetchFormTypeQuestions(formType.type_id);
  };
  
  // Close form questions modal
  const handleCloseFormQuestionsModal = () => {
    setShowFormQuestionsModal(false);
    setSelectedFormTypeForQuestions(null);
    setFormTypeQuestions([]);
  };
  
  // Open add/edit form question modal
  const handleOpenFormQuestionModal = (question = null) => {
    if (question) {
      setEditingFormQuestion(question);
      setFormQuestionForm({
        text: question.text,
        type: question.type,
        options: question.options || [],
        is_required: question.is_required ?? true
      });
    } else {
      setEditingFormQuestion(null);
      setFormQuestionForm({
        text: "",
        type: "text",
        options: [],
        is_required: true
      });
    }
    setNewFormOption("");
    setShowAddFormQuestionModal(true);
  };
  
  // Close form question modal
  const handleCloseFormQuestionModal = () => {
    setShowAddFormQuestionModal(false);
    setEditingFormQuestion(null);
    setFormQuestionForm({
      text: "",
      type: "text",
      options: [],
      is_required: true
    });
    setNewFormOption("");
  };
  
  // Add option to form question
  const handleAddFormOption = () => {
    if (newFormOption.trim() && !formQuestionForm.options.includes(newFormOption.trim())) {
      setFormQuestionForm({
        ...formQuestionForm,
        options: [...formQuestionForm.options, newFormOption.trim()]
      });
      setNewFormOption("");
    }
  };
  
  // Remove option from form question
  const handleRemoveFormOption = (optionToRemove) => {
    setFormQuestionForm({
      ...formQuestionForm,
      options: formQuestionForm.options.filter(opt => opt !== optionToRemove)
    });
  };
  
  // Save form question
  const handleSaveFormQuestion = async () => {
    if (!formQuestionForm.text.trim()) {
      toast.error(t('common:questionTextRequired'));
      return;
    }
    
    if ((formQuestionForm.type === "select" || formQuestionForm.type === "multiselect") && formQuestionForm.options.length < 2) {
      toast.error(t("surveys:toast_secimliSorularIcinEnAz"));
      return;
    }
    
    const typeId = selectedFormTypeForQuestions.type_id;
    
    try {
      const url = editingFormQuestion
        ? `${API}/member-forms/types/${typeId}/questions/${editingFormQuestion.question_id}`
        : `${API}/member-forms/types/${typeId}/questions`;
      
      const response = await authFetch(url, {
        method: editingFormQuestion ? "PUT" : "POST",
        body: JSON.stringify(formQuestionForm),
      });
      
      if (response.ok) {
        toast.success(editingFormQuestion ? t("surveys:questionUpdated") : t("surveys:questionAdded"));
        await fetchFormTypeQuestions(typeId);
        handleCloseFormQuestionModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  // Delete form question
  const handleDeleteFormQuestion = async (questionId, questionText = "") => {
    const typeId = selectedFormTypeForQuestions.type_id;
    setDeleteTarget({ 
      type: 'form-question', 
      id: questionId, 
      parentId: typeId,
      name: questionText 
    });
    setShowDeleteDialog(true);
  };
  
  // Helper function to make authenticated API calls
  const authFetch = async (url, options = {}) => {
    // Use cookie-based auth (credentials: include) - works with Google OAuth
    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
    });
  };

  const fetchSettings = async () => {
    try {
      console.log("Fetching admin settings...");
      const response = await authFetch(`${API}/admin/settings`);
      console.log("Admin settings response status:", response.status);
      if (response.ok) {
        const data = await response.json();
        console.log("Admin settings data:", data);
        setSettings(data);
        // Convert plans array to object for easier editing
        const plansObj = {};
        const plansArray = data.plans || [];
        console.log("Plans array from API:", plansArray);
        plansArray.forEach(p => {
          if (p && p.id) {
            plansObj[p.id] = p;
          }
        });
        console.log("Plans object after conversion:", plansObj);
        setPlans(plansObj);
        // Load global yearly discount from settings or first plan
        if (data.global_yearly_discount !== undefined) {
          setGlobalYearlyDiscount(data.global_yearly_discount);
        } else if (plansArray.length > 0) {
          // Get from first non-free plan
          const proPlan = plansArray.find(p => p.id !== "free" && p.yearly_discount);
          if (proPlan) {
            setGlobalYearlyDiscount(proPlan.yearly_discount || 17);
          }
        }
        // Handle branches - keep full objects with translations
        const branchesData = data.branches || [];
        const branchObjects = branchesData.map(b => {
          if (typeof b === 'object') return { ...b, translations: b.translations || {} };
          return { name: b, is_active: true, translations: {} };
        });
        console.log("Branches:", branchObjects);
        setBranches(branchObjects);
        // Handle training types - support both old string format and new object format
        const typesData = data.training_types || [];
        const normalizedTypes = typesData.map(t => {
          if (typeof t === 'object' && t.name) {
            return { name: t.name, max_participants: t.max_participants || 1, is_active: t.is_active !== false, translations: t.translations || {} };
          }
          return { name: t, max_participants: 1, is_active: true, translations: {} };
        });
        console.log("Training types:", normalizedTypes);
        setTrainingTypes(normalizedTypes);
        // Handle member cards
        const cardsData = data.member_cards || [];
        console.log("Member cards:", cardsData);
        setMemberCards(cardsData);
        // Handle measurement params
        const paramsData = data.measurement_params || [];
        console.log("Measurement params:", paramsData);
        setMeasurementParams(paramsData);
        // Handle expense types
        const expenseTypesData = data.expense_types || [];
        console.log("Expense types:", expenseTypesData);
        setExpenseTypes(expenseTypesData.map(t => ({...t, translations: t.translations || {}})));
      } else if (response.status === 403) {
        console.log("403 Forbidden - not admin");
      } else if (response.status === 401) {
        console.log("401 Unauthorized");
      }
      
      // Also fetch available languages
      try {
        const langRes = await fetch(`${API}/translations/public/languages`);
        if (langRes.ok) {
          const langData = await langRes.json();
          const langs = langData.languages || langData;
          setAvailableLanguages(langs.map(l => l.code || l));
        }
      } catch {}
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await authFetch(`${API}/admin/stats`);
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handlePlanChange = (planId, field, value) => {
    setPlans(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        [field]: field.includes("price") ? parseInt(value) || 0 : value
      }
    }));
  };

  const handleLimitChange = (planId, limitField, value) => {
    setPlans(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        limits: {
          ...prev[planId]?.limits,
          [limitField]: parseInt(value) || 0
        }
      }
    }));
  };

  const savePlans = async () => {
    setSaving(true);
    try {
      // Convert plans object back to array format
      // Apply global yearly discount to all paid plans
      const plansArray = Object.values(plans).map(p => ({
        ...p,
        yearly_discount: p.id === "free" ? 0 : globalYearlyDiscount,
        updated_at: new Date().toISOString()
      }));
      
      const response = await authFetch(`${API}/admin/settings/plans`, {
        method: "PUT",
        body: JSON.stringify({ 
          plans: plansArray,
          global_yearly_discount: globalYearlyDiscount
        }),
      });
      if (response.ok) {
        toast.success(t("admin:toast_planAyarlariKaydedildi"));
        fetchSettings();
        fetchPriceHistory(); // Refresh history after save
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:saveError"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  // Delete a specific plan (like Enterprise)
  const deletePlan = async (planId, planName = "") => {
    setDeletePlanDialog({ open: true, planId, planName: planName || planId });
  };

  const confirmDeletePlan = async () => {
    const planId = deletePlanDialog.planId;
    setDeletingPlan(true);
    
    try {
      const response = await authFetch(`${API}/admin/settings/plans/${planId}`, {
        method: "DELETE",
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || t("admin:planDeleted"));
        setDeletePlanDialog({ open: false, planId: null, planName: "" });
        fetchSettings(); // Refresh the plans list
      } else {
        const error = await response.json();
        toast.error(error.detail || "Plan silinemedi");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setDeletingPlan(false);
    }
  };

  const fetchPriceHistory = async () => {
    try {
      const response = await authFetch(`${API}/admin/settings/plans/history`);
      if (response.ok) {
        const data = await response.json();
        setPriceHistory(data.history || []);
      }
    } catch (error) {
      console.error("Error fetching price history:", error);
    }
  };

  // Branches functions
  const checkBranchUsage = async (branch) => {
    try {
      const response = await authFetch(`${API}/admin/check-branch-usage/${encodeURIComponent(branch)}`);
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error("Error checking branch usage:", error);
    }
    return { is_used: false };
  };

  const saveBranches = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/admin/settings/branches`, {
        method: "PUT",
        body: JSON.stringify({ branches }),
      });
      if (response.ok) {
        toast.success(t("common:toast_bransListesiKaydedildi"));
        fetchSettings();
        if (refreshData) refreshData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:saveError"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const addBranch = () => {
    if (newBranch.trim() && !branches.find(b => b.name === newBranch.trim())) {
      setBranches([...branches, { name: newBranch.trim(), is_active: true, translations: {} }]);
      setNewBranch("");
      setShowAddBranchModal(false);
    }
  };

  const removeBranch = async (branch) => {
    const branchName = typeof branch === 'object' ? branch.name : branch;
    const usage = await checkBranchUsage(branchName);
    if (usage.is_used) {
      toast.error(t("common:branchInUseCannotDelete", { businesses: usage.business_count, trainers: usage.trainer_count }));
      return;
    }
    setBranches(branches.filter(b => b.name !== branchName));
  };

  // Training types functions
  const checkTrainingTypeUsage = async (type) => {
    try {
      const response = await authFetch(`${API}/admin/check-training-type-usage/${encodeURIComponent(type)}`);
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error("Error checking training type usage:", error);
    }
    return { is_used: false };
  };

  const saveTrainingTypes = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/admin/settings/training-types`, {
        method: "PUT",
        body: JSON.stringify({ training_types: trainingTypes }),
      });
      if (response.ok) {
        toast.success("Antrenman tipleri kaydedildi");
        fetchSettings();
        if (refreshData) refreshData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:saveError"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const addTrainingType = () => {
    const name = newTrainingType.trim();
    if (name && !trainingTypes.some(t => t.name === name)) {
      setTrainingTypes([...trainingTypes, { 
        name: name, 
        max_participants: newTrainingTypeMaxParticipants || 1 
      }]);
      setNewTrainingType("");
      setNewTrainingTypeMaxParticipants(1);
      setShowAddTrainingTypeModal(false);
    }
  };

  const removeTrainingType = async (typeName) => {
    const usage = await checkTrainingTypeUsage(typeName);
    if (usage.is_used) {
      toast.error(`Bu antrenman tipi ${usage.reservation_count} rezervasyonda kullanılıyor. Silinemez.`);
      return;
    }
    setTrainingTypes(trainingTypes.filter(t => t.name !== typeName));
  };

  const updateTrainingTypeMaxParticipants = (typeName, maxParticipants) => {
    setTrainingTypes(trainingTypes.map(t => 
      t.name === typeName ? { ...t, max_participants: parseInt(maxParticipants) || 1 } : t
    ));
  };

  // Member cards functions
  const checkCardUsage = async (cardName) => {
    try {
      const response = await authFetch(`${API}/admin/check-card-usage/${encodeURIComponent(cardName)}`);
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error("Error checking card usage:", error);
    }
    return { is_used: false };
  };

  const saveMemberCards = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/admin/settings/member-cards`, {
        method: "PUT",
        body: JSON.stringify({ member_cards: memberCards }),
      });
      if (response.ok) {
        toast.success(t("common:cardListSaved"));
        fetchSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:saveError"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const addMemberCard = () => {
    if (newCardName.trim() && !memberCards.includes(newCardName.trim())) {
      setMemberCards([...memberCards, newCardName.trim()]);
      setNewCardName("");
      setShowAddCardModal(false);
    }
  };

  const removeMemberCard = async (cardName) => {
    const usage = await checkCardUsage(cardName);
    if (usage.is_used) {
      toast.error(`Bu kart ${usage.business_count} işletme tarafından kullanılıyor. Silinemez.`);
      return;
    }
    setMemberCards(memberCards.filter(c => c !== cardName));
  };

  // Measurement Params functions
  const addMeasurementParam = () => {
    if (newMeasurementParam.name.trim()) {
      const newParam = {
        id: `mparam_${Date.now()}`,
        name: newMeasurementParam.name.trim(),
        unit: newMeasurementParam.unit.trim(),
        is_active: true
      };
      setMeasurementParams([...measurementParams, newParam]);
      setNewMeasurementParam({ name: "", unit: "" });
      setShowAddMeasurementModal(false);
    }
  };

  const removeMeasurementParam = (paramId) => {
    setMeasurementParams(measurementParams.filter(p => p.id !== paramId));
  };

  const saveMeasurementParams = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/admin/settings/measurement-params`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ params: measurementParams }),
      });
      if (response.ok) {
        toast.success(t('members:measurementParamsSaved'));
        if (refreshData) refreshData();
      } else {
        toast.error(t('settings:saveFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  // Expense Types functions
  const addExpenseType = () => {
    if (!newExpenseType.trim()) {
      toast.error(t("reports:toast_giderTipiAdiGerekli"));
      return;
    }
    const newType = {
      id: `exp_${Date.now()}`,
      name: newExpenseType.trim(),
      is_active: true
    };
    setExpenseTypes([...expenseTypes, newType]);
    setNewExpenseType("");
    setShowAddExpenseTypeModal(false);
    toast.success(t("reports:toast_giderTipiEklendiKaydetmeyiUnutmayin"));
  };

  const removeExpenseType = (typeId) => {
    setExpenseTypes(expenseTypes.filter(t => t.id !== typeId));
  };

  const saveExpenseTypes = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/admin/settings/expense-types`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ expense_types: expenseTypes }),
      });
      if (response.ok) {
        toast.success(t('payments:expenseTypesSaved'));
        if (refreshData) refreshData();
      } else {
        toast.error(t('settings:saveFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  // ========== DYNAMIC PLAN & MODULE MANAGEMENT ==========
  
  // Fetch system modules
  const fetchSystemModules = async () => {
    try {
      const response = await authFetch(`${API}/admin/settings/modules`);
      if (response.ok) {
        const data = await response.json();
        setSystemModules(data.modules || []);
      }
    } catch (error) {
      console.error("Error fetching system modules:", error);
    }
  };

  // Create new plan
  const handleCreatePlan = async () => {
    if (!newPlanForm.name.trim()) {
      toast.error(t("admin:toast_planAdiGerekli"));
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch(`${API}/admin/settings/plans`, {
        method: "POST",
        body: JSON.stringify(newPlanForm),
      });

      if (response.ok) {
        toast.success(t("admin:newPlanCreated"));
        fetchSettings();
        setShowCreatePlanModal(false);
        setNewPlanForm({
          name: "",
          description: "",
          monthly_price: 0,
          yearly_price: 0,
          yearly_discount: 0,
          allowed_modules: [],
          limits: { members: -1 }
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || t("admin:planCreateFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  // Fetch revenue report
  const fetchRevenueReport = async (periodParam = null) => {
    setLoadingRevenue(true);
    try {
      let url = `${API}/admin/revenue-report`;
      const filterPeriod = periodParam || revenuePeriod;
      if (filterPeriod && filterPeriod !== "all") {
        url += `?period=${filterPeriod}`;
      }
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setRevenueReport(data);
      }
    } catch (error) {
      console.error("Error fetching revenue report:", error);
    } finally {
      setLoadingRevenue(false);
    }
  };

  // Initialize default plans with features
  const initializeDefaultPlans = async () => {
    try {
      await authFetch(`${API}/admin/settings/plans/initialize-defaults`, {
        method: "POST"
      });
    } catch (error) {
      console.error("Error initializing default plans:", error);
    }
  };

  // Open features modal for a plan
  const handleOpenFeaturesModal = (planId) => {
    const plan = plans[planId];
    setSelectedPlanForFeatures({
      id: planId,
      name: plan?.name || planId,
      display_features: plan?.display_features || []
    });
    setNewFeatureText("");
    setShowFeaturesModal(true);
  };

  // Add new feature
  const handleAddFeature = () => {
    if (!newFeatureText.trim() || !selectedPlanForFeatures) return;
    
    const newFeature = {
      name: newFeatureText.trim(),
      included: true,
      visible: true,
      translations: {}
    };
    
    setSelectedPlanForFeatures({
      ...selectedPlanForFeatures,
      display_features: [...(selectedPlanForFeatures.display_features || []), newFeature]
    });
    setNewFeatureText("");
  };

  // Toggle feature included status
  const handleToggleFeatureIncluded = (index) => {
    if (!selectedPlanForFeatures) return;
    
    const features = [...selectedPlanForFeatures.display_features];
    features[index] = { ...features[index], included: !features[index].included };
    
    setSelectedPlanForFeatures({
      ...selectedPlanForFeatures,
      display_features: features
    });
  };

  // Toggle feature visibility
  const handleToggleFeatureVisible = (index) => {
    if (!selectedPlanForFeatures) return;
    
    const features = [...selectedPlanForFeatures.display_features];
    features[index] = { ...features[index], visible: !features[index].visible };
    
    setSelectedPlanForFeatures({
      ...selectedPlanForFeatures,
      display_features: features
    });
  };

  // Delete feature
  const handleDeleteFeature = (index) => {
    if (!selectedPlanForFeatures) return;
    
    const features = selectedPlanForFeatures.display_features.filter((_, i) => i !== index);
    
    setSelectedPlanForFeatures({
      ...selectedPlanForFeatures,
      display_features: features
    });
  };

  // Save plan features
  const handleSavePlanFeatures = async () => {
    if (!selectedPlanForFeatures) return;

    setSaving(true);
    try {
      const response = await authFetch(`${API}/admin/settings/plans/${selectedPlanForFeatures.id}/features`, {
        method: "PUT",
        body: JSON.stringify({ display_features: selectedPlanForFeatures.display_features }),
      });

      if (response.ok) {
        toast.success(t("admin:toast_planOzellikleriGuncellendi"));
        // Update local state
        setPlans(prev => ({
          ...prev,
          [selectedPlanForFeatures.id]: {
            ...prev[selectedPlanForFeatures.id],
            display_features: selectedPlanForFeatures.display_features
          }
        }));
        setShowFeaturesModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:featuresNotSaved"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  // Open modules modal for a plan
  const handleOpenModulesModal = (planId) => {
    const plan = plans[planId];
    setSelectedPlanForModules({
      id: planId,
      name: plan?.name || planId,
      allowed_modules: plan?.allowed_modules || []
    });
    setShowModulesModal(true);
  };

  // Toggle module for a plan
  const handleToggleModule = (moduleId) => {
    if (!selectedPlanForModules) return;
    
    const currentModules = selectedPlanForModules.allowed_modules || [];
    const newModules = currentModules.includes(moduleId)
      ? currentModules.filter(m => m !== moduleId)
      : [...currentModules, moduleId];
    
    setSelectedPlanForModules({
      ...selectedPlanForModules,
      allowed_modules: newModules
    });
  };

  // Select/Deselect all modules
  const handleSelectAllModules = (select) => {
    if (!selectedPlanForModules) return;
    
    const newModules = select ? systemModules.map(m => m.id) : [];
    setSelectedPlanForModules({
      ...selectedPlanForModules,
      allowed_modules: newModules
    });
  };

  // Save plan modules
  const handleSavePlanModules = async () => {
    if (!selectedPlanForModules) return;

    setSaving(true);
    try {
      const response = await authFetch(`${API}/admin/settings/plans/${selectedPlanForModules.id}/modules`, {
        method: "PUT",
        body: JSON.stringify({ allowed_modules: selectedPlanForModules.allowed_modules }),
      });

      if (response.ok) {
        toast.success(t("admin:toast_planModulleriGuncellendi"));
        // Update local state
        setPlans(prev => ({
          ...prev,
          [selectedPlanForModules.id]: {
            ...prev[selectedPlanForModules.id],
            allowed_modules: selectedPlanForModules.allowed_modules
          }
        }));
        setShowModulesModal(false);
        
        // Dispatch event to refresh subscription in DashboardPage
        window.dispatchEvent(new CustomEvent('subscription-modules-updated'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t("admin:modulesNotSaved"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  // Group modules by category for display
  const MODULE_GROUPS = {
    genel: { label: "Genel", color: "bg-blue-100 text-blue-700" },
    uyeler: { label: t("common:memberManagement"), color: "bg-green-100 text-green-700" },
    rezervasyon: { label: t('common:reservation'), color: "bg-purple-100 text-purple-700" },
    odemeler: { label: t('common:payments'), color: "bg-orange-100 text-orange-700" },
    isletme: { label: t("settings:businessSettings"), color: "bg-pink-100 text-pink-700" },
    iletisim: { label: "İletişim", color: "bg-teal-100 text-teal-700" },
    harita: { label: "Harita Görünümü", color: "bg-cyan-100 text-cyan-700" }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  if (!settings) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-red-700">
            <AlertTriangle className="w-6 h-6" />
            <div>
              <p className="font-semibold">Admin Yetkisi Gerekli</p>
              <p className="text-sm">Bu sayfayı görüntülemek için admin yetkisine sahip olmalısınız.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy">{t("admin:adminAyarlari")}</h1>
          <p className="text-grey">{t("common:sistemAyarlariniYonetin")}</p>
        </div>
        <Button variant="outline" onClick={() => { fetchSettings(); fetchStats(); }} className="rounded-xl">
          <RefreshCw className="w-4 h-4 mr-2" />
          {t("common:refresh")}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy">{stats.total_users || stats.users || 0}</p>
                  <p className="text-sm text-grey">{t("common:toplamKullanici")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <Dumbbell className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy">{stats.total_businesses || stats.businesses || 0}</p>
                  <p className="text-sm text-grey">{t("common:toplamİsletme")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Crown className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy">
                    {stats.active_subscriptions || 0}
                  </p>
                  <p className="text-sm text-grey">{t("admin:activeSubscription")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy">{stats.pending_businesses || 0}</p>
                  <p className="text-sm text-grey">{t("common:bekleyenİsletme")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Settings Tabs */}
      <Tabs defaultValue="plans" className="w-full">
        <TabsList className="w-full justify-start rounded-xl bg-white p-1 shadow-sm flex-wrap h-auto gap-1">
          <TabsTrigger value="plans" className="rounded-lg"><Crown className="w-4 h-4 mr-2" />{t('admin:plans')}</TabsTrigger>
          <TabsTrigger value="revenue" className="rounded-lg"><BarChart3 className="w-4 h-4 mr-2" />{t('reports:revenueReport')}</TabsTrigger>
          <TabsTrigger value="branches" className="rounded-lg"><Dumbbell className="w-4 h-4 mr-2" />{t('common:branches')}</TabsTrigger>
          <TabsTrigger value="training-types" className="rounded-lg"><Activity className="w-4 h-4 mr-2" />{t('common:trainingTypes')}</TabsTrigger>
          <TabsTrigger value="cards" className="rounded-lg"><CreditCard className="w-4 h-4 mr-2" />{t("common:uyelikKartlari")}</TabsTrigger>
          <TabsTrigger value="measurement-params" className="rounded-lg"><BarChart3 className="w-4 h-4 mr-2" />{t("settings:measurementParams")}</TabsTrigger>
          <TabsTrigger value="expense-types" className="rounded-lg"><Receipt className="w-4 h-4 mr-2" />{t('settings:expenseTypes')}</TabsTrigger>
          <TabsTrigger value="survey-types" className="rounded-lg"><ClipboardList className="w-4 h-4 mr-2" />{t("surveys:anketTurleri")}</TabsTrigger>
          <TabsTrigger value="form-types" className="rounded-lg"><FileText className="w-4 h-4 mr-2" />{t("settings:formTypes")}</TabsTrigger>
        </TabsList>

        {/* Global Translation Language Selector */}
        {availableLanguages.filter(l => l !== "tr").length > 0 && (
          <div className="flex items-center gap-2 mt-4 mb-2">
            <span className="text-sm text-slate-500">{t("common:translationLang")}:</span>
            <select
              value={selectedTransLang}
              onChange={(e) => setSelectedTransLang(e.target.value)}
              className="h-8 px-2 text-sm border rounded-lg bg-white"
            >
              {availableLanguages.filter(l => l !== "tr").map(lang => (
                <option key={lang} value={lang}>{lang.toUpperCase()}</option>
              ))}
            </select>
          </div>
        )}

        {/* Revenue Report Tab */}
        <TabsContent value="revenue" className="mt-6">
          <div className="space-y-6">
            {/* Period Filter */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-grey">{t("common:period") || "Dönem"}:</span>
              {[
                { value: "all", label: t("common:all") },
                { value: "month", label: t("common:thisMonth") || "Bu Ay" },
                { value: "quarter", label: t("common:thisQuarter") },
                { value: "year", label: t("common:thisYear") }
              ].map(period => (
                <Button
                  key={period.value}
                  variant={revenuePeriod === period.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setRevenuePeriod(period.value);
                    fetchRevenueReport(period.value);
                  }}
                  className={`rounded-xl ${revenuePeriod === period.value ? "bg-navy" : ""}`}
                >
                  {period.label}
                </Button>
              ))}
            </div>
            
            {/* Summary Cards */}
            {revenueReport?.summary && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-700">
                          {revenueReport.summary.total_subscribers}
                        </p>
                        <p className="text-sm text-green-600">Toplam Abone</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-700">
                          {currencySymbol}{revenueReport.summary.total_monthly_equivalent?.toLocaleString('tr-TR')}
                        </p>
                        <p className="text-sm text-blue-600">{t("reports:monthlyIncome")}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-purple-100">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
                        <Crown className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-purple-700">
                          {currencySymbol}{revenueReport.summary.projected_annual_revenue?.toLocaleString('tr-TR')}
                        </p>
                        <p className="text-sm text-purple-600">{t("common:tahminiYillik")}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-orange-100">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-orange-700">
                          {currencySymbol}{revenueReport.summary.total_subscribers > 0 
                            ? Math.round(revenueReport.summary.total_monthly_equivalent / revenueReport.summary.total_subscribers).toLocaleString('tr-TR')
                            : 0}
                        </p>
                        <p className="text-sm text-orange-600">Ortalama/Abone</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Revenue by Plan */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Crown className="w-5 h-5 text-navy" />
                    Plan Bazlı Gelir Dağılımı
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchRevenueReport}
                    disabled={loadingRevenue}
                    className="rounded-xl"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingRevenue ? 'animate-spin' : ''}`} />
                    {t("common:refresh")}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingRevenue ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-navy" />
                  </div>
                ) : revenueReport?.by_plan?.length > 0 ? (
                  <div className="space-y-4">
                    {/* Desktop Table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 font-medium text-grey">Plan</th>
                            <th className="text-right py-3 px-4 font-medium text-grey">{t("common:aylikFiyat")}</th>
                            <th className="text-right py-3 px-4 font-medium text-grey">{t("common:yillikFiyat")}</th>
                            <th className="text-center py-3 px-4 font-medium text-grey">Abone</th>
                            <th className="text-right py-3 px-4 font-medium text-grey">{t("reports:monthlyIncome")}</th>
                            <th className="text-right py-3 px-4 font-medium text-grey">{t("reports:yillikGelir")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenueReport.by_plan.map((plan) => (
                            <tr key={plan.plan_id} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <Crown className={`w-4 h-4 ${plan.plan_id === 'pro' ? 'text-blue-600' : plan.plan_id === 'free' ? 'text-gray-400' : 'text-green-600'}`} />
                                  <span className="font-medium">{plan.plan_name}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-right">{currencySymbol}{plan.monthly_price?.toLocaleString('tr-TR')}</td>
                              <td className="py-3 px-4 text-right">{currencySymbol}{plan.yearly_price?.toLocaleString('tr-TR')}</td>
                              <td className="py-3 px-4 text-center">
                                <Badge variant="secondary">{plan.subscriber_count}</Badge>
                              </td>
                              <td className="py-3 px-4 text-right font-medium text-green-600">
                                {currencySymbol}{plan.monthly_revenue?.toLocaleString('tr-TR')}
                              </td>
                              <td className="py-3 px-4 text-right font-medium text-blue-600">
                                {currencySymbol}{plan.yearly_revenue?.toLocaleString('tr-TR')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3">
                      {revenueReport.by_plan.map((plan) => (
                        <Card key={plan.plan_id} className="border shadow-sm">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Crown className={`w-5 h-5 ${plan.plan_id === 'pro' ? 'text-blue-600' : plan.plan_id === 'free' ? 'text-gray-400' : 'text-green-600'}`} />
                                <span className="font-semibold text-navy">{plan.plan_name}</span>
                              </div>
                              <Badge variant="secondary">{plan.subscriber_count} abone</Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className="text-grey">{t("common:aylikFiyat")}</p>
                                <p className="font-medium">{currencySymbol}{plan.monthly_price?.toLocaleString('tr-TR')}</p>
                              </div>
                              <div>
                                <p className="text-grey">{t("common:yillikFiyat")}</p>
                                <p className="font-medium">{currencySymbol}{plan.yearly_price?.toLocaleString('tr-TR')}</p>
                              </div>
                              <div>
                                <p className="text-grey">{t("reports:monthlyIncome")}</p>
                                <p className="font-medium text-green-600">{currencySymbol}{plan.monthly_revenue?.toLocaleString('tr-TR')}</p>
                              </div>
                              <div>
                                <p className="text-grey">{t("reports:yillikGelir")}</p>
                                <p className="font-medium text-blue-600">{currencySymbol}{plan.yearly_revenue?.toLocaleString('tr-TR')}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-grey">
                    <Crown className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>{t("admin:henuzAbonelikVerisiYok")}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="mt-6">
          <div className="space-y-6">
            {/* Global Yearly Discount + Create New Plan Button */}
            <div className="flex items-center justify-between gap-4">
              <Card className="border-0 shadow-sm bg-green-50/50 flex-1">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-green-100 rounded-xl">
                      <Calendar className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-green-700 font-medium">{t("admin:yearlyDiscountRate")}</Label>
                      <p className="text-xs text-green-600">{t("admin:tumPlanlarIcinGecerliTek")}</p>
                    </div>
                    <Input 
                      type="number" 
                      value={globalYearlyDiscount} 
                      onChange={(e) => setGlobalYearlyDiscount(parseInt(e.target.value) || 0)}
                      className="rounded-xl w-24 text-center font-bold text-green-700 bg-white" 
                      min="0"
                      max="100"
                    />
                    <span className="text-green-600 font-medium">%</span>
                  </div>
                </CardContent>
              </Card>
              <Button onClick={() => setShowCreatePlanModal(true)} className="bg-navy hover:bg-navy/90 rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                {t("admin:newPlanCreate")}
              </Button>
            </div>
            
            {Object.keys(plans).length > 0 ? (
              Object.entries(plans).map(([planId, plan]) => (
                <Card key={planId} className="border-0 shadow-sm">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Crown className={`w-5 h-5 ${planId === "pro" || planId === "premium" ? "text-blue-600" : "text-gray-400"}`} />
                        {plan?.name || planId}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge className={planId === "pro" || planId === "premium" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"}>
                          {planId.toUpperCase()}
                        </Badge>
                        {/* Modules button */}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleOpenModulesModal(planId)}
                          className="rounded-xl"
                        >
                          <Layers className="w-4 h-4 mr-1" />
                          {t("admin:modules")} ({plan?.allowed_modules?.length || 0})
                        </Button>
                        {/* Features button */}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleOpenFeaturesModal(planId)}
                          className="rounded-xl"
                        >
                          <ClipboardList className="w-4 h-4 mr-1" />
                          {t("common:features")} ({plan?.display_features?.length || 0})
                        </Button>
                        {/* Delete button for non-essential plans */}
                        {planId !== "free" && planId !== "pro" && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => deletePlan(planId, plan?.name || planId)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Sil
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <Label>{t("admin:planName")}</Label>
                        <Input value={plan?.name || ""} onChange={(e) => handlePlanChange(planId, "name", e.target.value)} className="rounded-xl mt-1" />
                      </div>
                      <div>
                        <Label>{t("common:monthlyPriceTL")}</Label>
                        <Input type="number" value={plan?.monthly_price || plan?.price || 0} onChange={(e) => handlePlanChange(planId, "monthly_price", e.target.value)} className="rounded-xl mt-1" disabled={planId === "free"} />
                      </div>
                      <div>
                        <Label>{t("common:yearlyPriceTL")}</Label>
                        <Input type="number" value={plan?.yearly_price || 0} onChange={(e) => handlePlanChange(planId, "yearly_price", e.target.value)} className="rounded-xl mt-1" disabled={planId === "free"} />
                      </div>
                      <div>
                        <Label>{t("common:memberLimitUnlimited")}</Label>
                        <Input type="number" value={plan?.limits?.members ?? 0} onChange={(e) => handleLimitChange(planId, "members", e.target.value)} className="rounded-xl mt-1" />
                      </div>
                    </div>
                    
                    {/* Show assigned modules */}
                    {plan?.allowed_modules?.length > 0 && (
                      <div className="pt-2 border-t">
                        <p className="text-xs text-grey mb-2">{t("admin:activeModules")}:</p>
                        <div className="flex flex-wrap gap-1">
                          {plan.allowed_modules.slice(0, 8).map(moduleId => {
                            const mod = systemModules.find(m => m.id === moduleId);
                            return (
                              <Badge key={moduleId} variant="secondary" className="text-xs">
                                {mod?.name || moduleId}
                              </Badge>
                            );
                          })}
                          {plan.allowed_modules.length > 8 && (
                            <Badge variant="secondary" className="text-xs">
                              +{plan.allowed_modules.length - 8} daha
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-8 text-center">
                  <Crown className="w-12 h-12 mx-auto text-grey mb-4" />
                  <p className="text-grey">{t("admin:henuzPlanTanimlanmamis")}</p>
                </CardContent>
              </Card>
            )}
            <div className="flex justify-end gap-2">
              <Button onClick={savePlans} disabled={saving} className="bg-navy hover:bg-navy/90 rounded-xl">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {t("admin:savePlans")}
              </Button>
              <Button variant="outline" onClick={() => setShowHistoryModal(true)} className="rounded-xl">
                <History className="w-4 h-4 mr-2" />
                {t("common:priceHistory")}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Branches Tab */}
        <TabsContent value="branches" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{t("common:sporBranslari")}</CardTitle>
                  <CardDescription>{t("common:i̇sletmelerinSecebilecegiBransListesi")}</CardDescription>
                </div>
                <Button onClick={() => setShowAddBranchModal(true)} className="bg-navy hover:bg-navy/90 rounded-xl">
                  <Plus className="w-4 h-4 mr-2" />{t("common:branchAdded").replace(" eklendi","") || "Branş"} {t("common:add") || "Ekle"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {branches.length > 0 ? (
                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">TR</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">{selectedTransLang.toUpperCase()}</th>
                        <th className="text-right p-3 text-sm font-medium text-slate-600 w-16">{t("common:i̇slem")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {branches.map((branch, idx) => (
                        <tr key={branch.name + idx} className="hover:bg-slate-50">
                          <td className="p-2">
                            <Input
                              className="h-9 text-sm rounded-lg"
                              value={branch.name}
                              onChange={(e) => {
                                const updated = [...branches];
                                updated[idx] = { ...updated[idx], name: e.target.value };
                                setBranches(updated);
                              }}
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              className="h-9 text-sm rounded-lg"
                              placeholder={`${selectedTransLang.toUpperCase()} ${t("common:featureTranslation").toLowerCase()}`}
                              value={branch.translations?.[selectedTransLang] || ""}
                              onChange={(e) => {
                                const updated = [...branches];
                                if (!updated[idx].translations) updated[idx].translations = {};
                                updated[idx].translations[selectedTransLang] = e.target.value;
                                setBranches(updated);
                              }}
                            />
                          </td>
                          <td className="p-2 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeBranch(branch)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-grey">{t("common:henuzBransEklenmemis")}</div>
              )}

              <div className="flex justify-end mt-6">
                <Button onClick={saveBranches} disabled={saving} className="bg-navy hover:bg-navy/90 rounded-xl">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {t("common:saveBranches")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Training Types Tab */}
        <TabsContent value="training-types" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{t("common:trainingTypes")}</CardTitle>
                  <CardDescription>{t("events:rezervasyonlardaSecilebilecekAntrenmanTu")}</CardDescription>
                </div>
                <Button onClick={() => setShowAddTrainingTypeModal(true)} className="bg-navy hover:bg-navy/90 rounded-xl">
                  <Plus className="w-4 h-4 mr-2" />{t("common:add") || "Ekle"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {trainingTypes.length > 0 ? (
                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">TR</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">{selectedTransLang.toUpperCase()}</th>
                        <th className="text-center p-3 text-sm font-medium text-slate-600 w-24">Max</th>
                        <th className="text-right p-3 text-sm font-medium text-slate-600 w-16">{t("common:i̇slem")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {trainingTypes.map((type, idx) => (
                        <tr key={type.name + idx} className="hover:bg-slate-50">
                          <td className="p-2">
                            <Input
                              className="h-9 text-sm rounded-lg"
                              value={type.name}
                              onChange={(e) => {
                                const updated = [...trainingTypes];
                                updated[idx] = { ...updated[idx], name: e.target.value };
                                setTrainingTypes(updated);
                              }}
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              className="h-9 text-sm rounded-lg"
                              placeholder={`${selectedTransLang.toUpperCase()} ${t("common:featureTranslation").toLowerCase()}`}
                              value={type.translations?.[selectedTransLang] || ""}
                              onChange={(e) => {
                                const updated = [...trainingTypes];
                                if (!updated[idx].translations) updated[idx].translations = {};
                                updated[idx].translations[selectedTransLang] = e.target.value;
                                setTrainingTypes(updated);
                              }}
                            />
                          </td>
                          <td className="p-2 text-center">
                            <Input 
                              type="number" 
                              min="1" 
                              max="999"
                              value={type.max_participants || 1}
                              onChange={(e) => updateTrainingTypeMaxParticipants(type.name, e.target.value)}
                              className="w-20 h-9 text-center mx-auto rounded-lg"
                            />
                          </td>
                          <td className="p-2 text-right">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removeTrainingType(type.name)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-grey">{t("common:noTrainingTypeAdded")}</div>
              )}

              <div className="flex justify-end mt-6">
                <Button onClick={saveTrainingTypes} disabled={saving} className="bg-navy hover:bg-navy/90 rounded-xl">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {t("common:saveTrainingTypes")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Member Cards Tab */}
        <TabsContent value="cards" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{t("common:uyelikKartlari")}</CardTitle>
                  <CardDescription>{t("common:memberCardDesc")}</CardDescription>
                </div>
                <Button onClick={() => setShowAddCardModal(true)} className="bg-navy hover:bg-navy/90 rounded-xl">
                  <Plus className="w-4 h-4 mr-2" />{t("common:add")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {memberCards.map((card) => (
                  <Badge key={card} variant="secondary" className="px-3 py-1.5 text-sm flex items-center gap-2 bg-blue-50 text-blue-700">
                    <CreditCard className="w-3 h-3" />
                    {card}
                    <button onClick={() => removeMemberCard(card)} className="hover:text-red-600 transition-colors"><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
              {memberCards.length === 0 && <div className="text-center py-8 text-grey">{t("common:noCardsYet")}</div>}
              <div className="flex justify-end mt-6">
                <Button onClick={saveMemberCards} disabled={saving} className="bg-navy hover:bg-navy/90 rounded-xl">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {t("common:saveCards")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Measurement Params Tab */}
        <TabsContent value="measurement-params" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold">{t("settings:measurementParams")}</CardTitle>
                  <CardDescription>{t("evaluations:i̇sletmelerinKullanabilecegiOlcumParamet")}</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowAddMeasurementModal(true)} 
                  className="rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t("common:addParameter")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {measurementParams.length === 0 ? (
                  <div className="text-center py-8 text-grey">
                    <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>{t("evaluations:henuzOlcumParametresiEklenmemis")}</p>
                    <Button 
                      variant="link" 
                      onClick={() => setShowAddMeasurementModal(true)}
                      className="text-navy"
                    >
                      {t("common:addFirstParameter")}
                    </Button>
                  </div>
                ) : (
                  <div className="border rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">TR</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">{selectedTransLang.toUpperCase()}</th>
                          <th className="text-center p-3 text-sm font-medium text-slate-600 w-24">{t("common:unit")}</th>
                          <th className="text-right p-3 text-sm font-medium text-slate-600 w-16">{t("common:i̇slem")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {measurementParams.map((param, idx) => (
                          <tr key={param.id} className="hover:bg-slate-50">
                            <td className="p-2">
                              <Input
                                className="h-9 text-sm rounded-lg"
                                value={param.name}
                                onChange={(e) => {
                                  const updated = [...measurementParams];
                                  updated[idx] = { ...updated[idx], name: e.target.value };
                                  setMeasurementParams(updated);
                                }}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                className="h-9 text-sm rounded-lg"
                                placeholder={`${selectedTransLang.toUpperCase()} ${t("common:featureTranslation").toLowerCase()}`}
                                value={param.translations?.[selectedTransLang] || ""}
                                onChange={(e) => {
                                  const updated = [...measurementParams];
                                  if (!updated[idx].translations) updated[idx].translations = {};
                                  updated[idx].translations[selectedTransLang] = e.target.value;
                                  setMeasurementParams(updated);
                                }}
                              />
                            </td>
                            <td className="p-2 text-center">
                              <Input
                                className="h-9 text-sm rounded-lg w-20 mx-auto text-center"
                                value={param.unit || ""}
                                onChange={(e) => {
                                  const updated = [...measurementParams];
                                  updated[idx] = { ...updated[idx], unit: e.target.value };
                                  setMeasurementParams(updated);
                                }}
                              />
                            </td>
                            <td className="p-2 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:bg-red-50"
                                onClick={() => removeMeasurementParam(param.id)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <Button 
                  onClick={saveMeasurementParams} 
                  disabled={saving} 
                  className="w-full bg-navy hover:bg-navy/90 rounded-xl mt-4"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {t("common:saveParameters")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expense Types Tab */}
        <TabsContent value="expense-types" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{t("reports:i̇sletmeGiderTipleri")}</CardTitle>
                  <CardDescription>{t("reports:i̇sletmelerinKullanabilecegiGiderTipleri")}</CardDescription>
                </div>
                <Button onClick={() => setShowAddExpenseTypeModal(true)} className="bg-navy hover:bg-navy/90 rounded-xl" size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  {t("common:add") || "Ekle"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <p className="text-xs text-amber-600 mb-2">{t("common:otherOptionAlwaysAvailable")}</p>
                {expenseTypes.filter(t => t.id !== "diger").length === 0 ? (
                  <p className="text-grey text-center py-4">{t("reports:henuzGiderTipiEklenmemis")}</p>
                ) : (
                  <div className="border rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">TR</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">{selectedTransLang.toUpperCase()}</th>
                          <th className="text-right p-3 text-sm font-medium text-slate-600 w-16">{t("common:i̇slem")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {expenseTypes.filter(t => t.id !== "diger").map((type) => {
                          const realIdx = expenseTypes.findIndex(t => t.id === type.id);
                          return (
                            <tr key={type.id} className="hover:bg-slate-50">
                              <td className="p-2">
                                <Input
                                  className="h-9 text-sm rounded-lg"
                                  value={type.name}
                                  onChange={(e) => {
                                    const updated = [...expenseTypes];
                                    updated[realIdx] = { ...updated[realIdx], name: e.target.value };
                                    setExpenseTypes(updated);
                                  }}
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  className="h-9 text-sm rounded-lg"
                                  placeholder={`${selectedTransLang.toUpperCase()} ${t("common:featureTranslation").toLowerCase()}`}
                                  value={type.translations?.[selectedTransLang] || ""}
                                  onChange={(e) => {
                                    const updated = [...expenseTypes];
                                    if (!updated[realIdx].translations) updated[realIdx].translations = {};
                                    updated[realIdx].translations[selectedTransLang] = e.target.value;
                                    setExpenseTypes(updated);
                                  }}
                                />
                              </td>
                              <td className="p-2 text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeExpenseType(type.id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <Button 
                  onClick={saveExpenseTypes} 
                  disabled={saving} 
                  className="w-full bg-navy hover:bg-navy/90 rounded-xl mt-4"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {t("reports:saveExpenseTypes")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Survey Types Tab */}
        <TabsContent value="survey-types" className="mt-6">
          <Card>
            <CardHeader className="flex-row items-center justify-between pb-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  Anket Türleri
                </CardTitle>
                <p className="text-sm text-grey mt-1">
                  Tüm işletmelerin kullanabileceği anket şablonları
                </p>
              </div>
              <Button onClick={() => handleOpenSurveyTypeModal()} className="bg-navy hover:bg-navy/90 rounded-xl" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Yeni Tür
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {surveyTypes.length === 0 ? (
                  <p className="text-grey text-center py-4">{t("surveys:henuzAnketTuruEklenmemis")}</p>
                ) : (
                  <div className="grid gap-3">
                    {surveyTypes.map((type) => {
                      const IconComp = getIconComponent(type.icon);
                      return (
                        <div key={type.type_id} className="flex items-center justify-between bg-slate-50 rounded-xl p-4 group">
                          <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${getColorClass(type.color)}`}>
                              <IconComp className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-navy">{getLocalTrainingType(type)}</span>
                                {type.is_system && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Sistem</span>
                                )}
                                {type.usage_count > 0 && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                    {type.usage_count} işletme kullanıyor
                                  </span>
                                )}
                              </div>
                              {type.description && (
                                <p className="text-sm text-grey mt-1">{type.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-xl"
                              onClick={() => handleOpenQuestionsModal(type)}
                            >
                              <FileQuestion className="w-4 h-4 mr-1" />
                              Sorular
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleOpenSurveyTypeModal(type)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleDeleteSurveyType(type.type_id, type.name)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Form Types Tab */}
        <TabsContent value="form-types" className="mt-6">
          <Card>
            <CardHeader className="flex-row items-center justify-between pb-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Form Türleri
                </CardTitle>
                <p className="text-sm text-grey mt-1">
                  Zorunlu üye formları (PAR-Q, Tanışma Formu vb.)
                </p>
              </div>
              <Button onClick={() => handleOpenFormTypeModal()} className="bg-navy hover:bg-navy/90 rounded-xl" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Yeni Form Türü
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {formTypes.length === 0 ? (
                  <p className="text-grey text-center py-4">{t("surveys:henuzFormTuruEklenmemis")}</p>
                ) : (
                  <div className="grid gap-3">
                    {formTypes.map((type) => {
                      const IconComp = getIconComponent(type.icon);
                      return (
                        <div key={type.type_id} className="flex items-center justify-between bg-slate-50 rounded-xl p-4 group">
                          <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${getColorClass(type.color || "orange")}`}>
                              <IconComp className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-navy">{getLocalTrainingType(type)}</span>
                                {type.is_system && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Sistem</span>
                                )}
                                {type.is_required && (
                                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Zorunlu</span>
                                )}
                                <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded">
                                  {type.questions?.length || 0} soru
                                </span>
                              </div>
                              {type.description && (
                                <p className="text-sm text-grey mt-1">{type.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-xl"
                              onClick={() => handleOpenFormQuestionsModal(type)}
                            >
                              <FileQuestion className="w-4 h-4 mr-1" />
                              Sorular
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleOpenFormTypeModal(type)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleDeleteFormType(type.type_id, type.name)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Branch Modal */}
      <Dialog open={showAddBranchModal} onOpenChange={setShowAddBranchModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('settings:addNewBranch')}</DialogTitle></DialogHeader>
          <div className="py-4">
            <Label>{t("common:branchName")}</Label>
            <Input value={newBranch} onChange={(e) => setNewBranch(e.target.value)} placeholder={t("common:ph_ornYogaPilatesBoks")} className="rounded-xl mt-1" onKeyDown={(e) => e.key === "Enter" && addBranch()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddBranchModal(false)} className="rounded-xl">{t('common:cancel')}</Button>
            <Button onClick={addBranch} disabled={!newBranch.trim()} className="bg-navy hover:bg-navy/90 rounded-xl"><Plus className="w-4 h-4 mr-2" />{t('common:add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Training Type Modal */}
      <Dialog open={showAddTrainingTypeModal} onOpenChange={setShowAddTrainingTypeModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Yeni Antrenman Tipi Ekle</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>{t("common:trainingType")}</Label>
              <Input value={newTrainingType} onChange={(e) => setNewTrainingType(e.target.value)} placeholder={t("common:ph_ornBirebirDuetGrup")} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label>{t("common:maxCapacity")}</Label>
              <p className="text-xs text-grey mb-1">Aynı saat/oda/antrenörde kaç kişi rezervasyon yapabilir?</p>
              <Input 
                type="number" 
                min="1" 
                max="999"
                value={newTrainingTypeMaxParticipants} 
                onChange={(e) => setNewTrainingTypeMaxParticipants(parseInt(e.target.value) || 1)} 
                placeholder={t("common:ph_orn1210")} 
                className="rounded-xl mt-1" 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddTrainingTypeModal(false); setNewTrainingType(""); setNewTrainingTypeMaxParticipants(1); }} className="rounded-xl">{t('common:cancel')}</Button>
            <Button onClick={addTrainingType} disabled={!newTrainingType.trim()} className="bg-navy hover:bg-navy/90 rounded-xl"><Plus className="w-4 h-4 mr-2" />{t('common:add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Card Modal */}
      <Dialog open={showAddCardModal} onOpenChange={setShowAddCardModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("common:yeniUyelikKartiEkle")}</DialogTitle></DialogHeader>
          <div className="py-4">
            <Label>{t("common:cardName")}</Label>
            <Input value={newCardName} onChange={(e) => setNewCardName(e.target.value)} placeholder={t("common:ph_ornMultisportGympass")} className="rounded-xl mt-1" onKeyDown={(e) => e.key === "Enter" && addMemberCard()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCardModal(false)} className="rounded-xl">{t('common:cancel')}</Button>
            <Button onClick={addMemberCard} disabled={!newCardName.trim()} className="bg-navy hover:bg-navy/90 rounded-xl"><Plus className="w-4 h-4 mr-2" />{t('common:add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Measurement Param Modal */}
      <Dialog open={showAddMeasurementModal} onOpenChange={setShowAddMeasurementModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("evaluations:yeniOlcumParametresiEkle")}</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>{t("common:parameterNameRequired")}</Label>
              <Input 
                value={newMeasurementParam.name} 
                onChange={(e) => setNewMeasurementParam({...newMeasurementParam, name: e.target.value})} 
                placeholder={t("common:ph_ornKiloBoyVucutYag")} 
                className="rounded-xl mt-1" 
              />
            </div>
            <div>
              <Label>{t("common:unit")}</Label>
              <Input 
                value={newMeasurementParam.unit} 
                onChange={(e) => setNewMeasurementParam({...newMeasurementParam, unit: e.target.value})} 
                placeholder={t("common:ph_ornKgCm")} 
                className="rounded-xl mt-1" 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMeasurementModal(false)} className="rounded-xl">{t('common:cancel')}</Button>
            <Button onClick={addMeasurementParam} disabled={!newMeasurementParam.name.trim()} className="bg-navy hover:bg-navy/90 rounded-xl"><Plus className="w-4 h-4 mr-2" />{t('common:add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Expense Type Modal */}
      <Dialog open={showAddExpenseTypeModal} onOpenChange={setShowAddExpenseTypeModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('settings:addNewExpenseType')}</DialogTitle></DialogHeader>
          <div className="py-4">
            <Label>{t("reports:expenseTypeName")}</Label>
            <Input 
              value={newExpenseType} 
              onChange={(e) => setNewExpenseType(e.target.value)} 
              placeholder={t("common:ph_ornElektrikSuDogalgaz")} 
              className="rounded-xl mt-1" 
              onKeyDown={(e) => e.key === "Enter" && addExpenseType()} 
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddExpenseTypeModal(false)} className="rounded-xl">{t('common:cancel')}</Button>
            <Button onClick={addExpenseType} disabled={!newExpenseType.trim()} className="bg-navy hover:bg-navy/90 rounded-xl"><Plus className="w-4 h-4 mr-2" />{t('common:add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Fiyat Değişiklik Geçmişi
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {priceHistory.length > 0 ? (
              <div className="space-y-3">
                {priceHistory.map((entry, index) => (
                  <Card key={index} className="border shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge className="mb-2">{entry.plan_name || entry.plan_id}</Badge>
                          <div className="text-sm space-y-1">
                            <p>
                              <span className="text-grey">Aylık:</span>{" "}
                              <span className="line-through text-red-500">{currencySymbol}{entry.old_monthly_price}</span>{" "}
                              → <span className="text-green-600 font-medium">{currencySymbol}{entry.new_monthly_price}</span>
                            </p>
                            <p>
                              <span className="text-grey">Yıllık:</span>{" "}
                              <span className="line-through text-red-500">{currencySymbol}{entry.old_yearly_price}</span>{" "}
                              → <span className="text-green-600 font-medium">{currencySymbol}{entry.new_yearly_price}</span>
                            </p>
                            {(entry.old_yearly_discount !== entry.new_yearly_discount) && (
                              <p>
                                <span className="text-grey">İndirim:</span>{" "}
                                <span className="line-through text-red-500">%{entry.old_yearly_discount}</span>{" "}
                                → <span className="text-green-600 font-medium">%{entry.new_yearly_discount}</span>
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-xs text-grey">
                          {formatDateTimeTR(entry.changed_at)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-grey">
                <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{t("common:henuzFiyatDegisikligiYapilmamis")}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryModal(false)} className="rounded-xl">{t('common:close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Survey Type Modal */}
      <Dialog open={showAddSurveyTypeModal} onOpenChange={setShowAddSurveyTypeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingSurveyType ? t('common:editSurveyType') : t('common:addNewSurveyType')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>{t("common:typeName")}</Label>
              <Input
                value={surveyTypeForm.name}
                onChange={(e) =>
                  setSurveyTypeForm({ ...surveyTypeForm, name: e.target.value })
                }
                placeholder={t("surveys:ph_ornMemnuniyetAnketi")}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label>{t('common:description')}</Label>
              <Input
                value={surveyTypeForm.description}
                onChange={(e) =>
                  setSurveyTypeForm({
                    ...surveyTypeForm,
                    description: e.target.value,
                  })
                }
                placeholder={t("common:ph_kisaBirAciklama")}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label className="block mb-2">{t("common:icon")}</Label>
              <div className="flex flex-wrap gap-2">
                {SURVEY_ICONS.map((iconItem) => {
                  const IconComp = iconItem.icon;
                  return (
                    <button
                      key={iconItem.name}
                      type="button"
                      onClick={() =>
                        setSurveyTypeForm({ ...surveyTypeForm, icon: iconItem.name })
                      }
                      className={`p-2 rounded-xl border transition-colors ${
                        surveyTypeForm.icon === iconItem.name
                          ? "bg-navy text-white border-navy"
                          : "hover:bg-slate-100 border-slate-200"
                      }`}
                      title={iconItem.label}
                    >
                      <IconComp className="w-5 h-5" />
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="block mb-2">{t("common:color")}</Label>
              <div className="flex flex-wrap gap-2">
                {SURVEY_COLORS.map((colorItem) => (
                  <button
                    key={colorItem.name}
                    type="button"
                    onClick={() =>
                      setSurveyTypeForm({ ...surveyTypeForm, color: colorItem.name })
                    }
                    className={`p-3 rounded-xl border-2 transition-all ${
                      surveyTypeForm.color === colorItem.name
                        ? "border-navy scale-105"
                        : "border-transparent"
                    } ${colorItem.class}`}
                    title={colorItem.label}
                  >
                    <div className="w-4 h-4" />
                  </button>
                ))}
              </div>
            </div>
            {/* Preview */}
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-xs text-grey mb-2">Önizleme:</p>
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl ${getColorClass(surveyTypeForm.color)}`}>
                  {(() => {
                    const IconComp = getIconComponent(surveyTypeForm.icon);
                    return <IconComp className="w-5 h-5" />;
                  })()}
                </div>
                <div>
                  <p className="font-semibold text-navy">
                    {surveyTypeForm.name || t("surveys:surveyType")}
                  </p>
                  <p className="text-sm text-grey">
                    {surveyTypeForm.description || t('common:description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseSurveyTypeModal}
              className="rounded-xl"
            >
              İptal
            </Button>
            <Button
              onClick={handleSaveSurveyType}
              disabled={!surveyTypeForm.name.trim()}
              className="bg-navy hover:bg-navy/90 rounded-xl"
            >
              {editingSurveyType ? (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Güncelle
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Ekle
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Survey Type Questions Modal */}
      <Dialog open={showQuestionsModal} onOpenChange={setShowQuestionsModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileQuestion className="w-5 h-5" />
              {selectedSurveyTypeForQuestions?.name} - Sorular
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {/* Info Banner */}
            <div className="mb-4 p-3 bg-amber-50 rounded-xl">
              <p className="text-sm text-amber-800">
                ⚠️ Burada yaptığınız değişiklikler yeni aktivasyonlarda yansıyacaktır. 
                İşletmelerin mevcut kayıtlı anketleri ve cevapları etkilenmez.
              </p>
            </div>
            
            {loadingQuestions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-navy" />
              </div>
            ) : surveyTypeQuestions.length === 0 ? (
              <div className="text-center py-8 text-grey">
                <FileQuestion className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>{t("surveys:noQuestionsYet")}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {surveyTypeQuestions.map((question, index) => (
                  <div
                    key={question.question_id}
                    className="flex items-start justify-between p-4 bg-slate-50 rounded-xl group"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs bg-navy/10 text-navy px-2 py-0.5 rounded">
                          {index + 1}
                        </span>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          {getQuestionTypeLabel(question.type)}
                        </span>
                        {question.is_required && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                            Zorunlu
                          </span>
                        )}
                      </div>
                      <p className="font-medium text-navy">{question.text}</p>
                      {question.type === "multiple_choice" && question.options?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {question.options.map((opt, i) => (
                            <span key={i} className="text-xs bg-grey-100 text-grey-700 px-2 py-1 rounded">
                              {opt}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 ml-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleOpenQuestionModal(question)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDeleteQuestion(question.question_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="flex justify-between">
            <Button
              onClick={() => handleOpenQuestionModal()}
              className="bg-green-600 hover:bg-green-700 rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              Soru Ekle
            </Button>
            <Button
              variant="outline"
              onClick={handleCloseQuestionsModal}
              className="rounded-xl"
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Question Modal */}
      <Dialog open={showAddQuestionModal} onOpenChange={setShowAddQuestionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion ? t('surveys:editQuestion') : t('common:addNewQuestion')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>{t("surveys:questionText")}</Label>
              <Input
                value={questionForm.text}
                onChange={(e) => setQuestionForm({ ...questionForm, text: e.target.value })}
                placeholder="Soru metnini girin"
                className="rounded-xl mt-1"
              />
            </div>
            
            <div>
              <Label>{t("surveys:questionType")}</Label>
              <select
                value={questionForm.type}
                onChange={(e) => setQuestionForm({ 
                  ...questionForm, 
                  type: e.target.value,
                  options: e.target.value === "multiple_choice" ? questionForm.options : []
                })}
                className="w-full mt-1 p-2 border rounded-xl"
              >
                {QUESTION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            
            {questionForm.type === "multiple_choice" && (
              <div>
                <Label>{t("common:options")}</Label>
                <div className="mt-2 space-y-2">
                  {questionForm.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="flex-1 p-2 bg-slate-50 rounded-lg text-sm">{option}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500"
                        onClick={() => handleRemoveOption(option)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      placeholder={t("common:newOption")}
                      className="rounded-xl flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption())}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddOption}
                      className="rounded-xl"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
              <input
                type="checkbox"
                id="is_required"
                checked={questionForm.is_required}
                onChange={(e) => setQuestionForm({ ...questionForm, is_required: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="is_required" className="cursor-pointer">
                Bu soru zorunlu
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseQuestionModal}
              className="rounded-xl"
            >
              İptal
            </Button>
            <Button
              onClick={handleSaveQuestion}
              disabled={!questionForm.text.trim()}
              className="bg-navy hover:bg-navy/90 rounded-xl"
            >
              {editingQuestion ? (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Güncelle
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Ekle
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Form Type Create/Edit Modal */}
      <Dialog open={showAddFormTypeModal} onOpenChange={setShowAddFormTypeModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {editingFormType ? t("surveys:editFormType") : t("surveys:newFormType")}
            </DialogTitle>
            <DialogDescription>
              Üyelerin doldurması gereken zorunlu form şablonu oluşturun
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>{t("surveys:formTypeName")}</Label>
              <Input
                value={formTypeForm.name}
                onChange={(e) => setFormTypeForm({ ...formTypeForm, name: e.target.value })}
                placeholder={t("surveys:ph_ornSaglikFormu")}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label>{t('common:description')}</Label>
              <Input
                value={formTypeForm.description}
                onChange={(e) => setFormTypeForm({ ...formTypeForm, description: e.target.value })}
                placeholder={t("surveys:ph_buFormunAmaci")}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label>{t("common:icon")}</Label>
              <div className="grid grid-cols-5 gap-2 mt-2">
                {SURVEY_ICONS.map((iconItem) => (
                  <Button
                    key={iconItem.name}
                    type="button"
                    variant={formTypeForm.icon === iconItem.name ? "default" : "outline"}
                    className={`w-full h-12 rounded-xl ${formTypeForm.icon === iconItem.name ? "bg-navy" : ""}`}
                    onClick={() => setFormTypeForm({ ...formTypeForm, icon: iconItem.name })}
                  >
                    <iconItem.icon className="w-5 h-5" />
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>{t("common:color")}</Label>
              <div className="flex gap-2 mt-2">
                {SURVEY_COLORS.map((color) => (
                  <Button
                    key={color.name}
                    type="button"
                    variant="outline"
                    className={`w-10 h-10 rounded-xl ${color.bg} ${formTypeForm.color === color.name ? "ring-2 ring-navy ring-offset-2" : ""}`}
                    onClick={() => setFormTypeForm({ ...formTypeForm, color: color.name })}
                  >
                    {formTypeForm.color === color.name && <Check className={`w-4 h-4 ${color.text}`} />}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseFormTypeModal} className="rounded-xl">
              İptal
            </Button>
            <Button
              onClick={handleSaveFormType}
              disabled={!formTypeForm.name.trim()}
              className="bg-navy hover:bg-navy/90 rounded-xl"
            >
              <Save className="w-4 h-4 mr-2" />
              {editingFormType ? t('settings:update') : t('common:save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Form Type Questions Modal */}
      <Dialog open={showFormQuestionsModal} onOpenChange={setShowFormQuestionsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileQuestion className="w-5 h-5" />
              {selectedFormTypeForQuestions?.name} - Sorular
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-grey">
                Bu formdaki soruları düzenleyin
              </p>
              <Button
                onClick={() => handleOpenFormQuestionModal()}
                className="bg-navy hover:bg-navy/90 rounded-xl"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Soru Ekle
              </Button>
            </div>
            
            {loadingFormQuestions ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-navy" />
              </div>
            ) : formTypeQuestions.length === 0 ? (
              <p className="text-center text-grey py-8">{t("surveys:noQuestionsYet")}</p>
            ) : (
              <div className="space-y-3">
                {formTypeQuestions.map((question, index) => (
                  <div
                    key={question.question_id}
                    className="flex items-start justify-between bg-slate-50 rounded-xl p-4 group"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-grey">#{index + 1}</span>
                        <Badge variant="outline" className="text-xs">
                          {getQuestionTypeLabel(question.type)}
                        </Badge>
                        {question.is_required && (
                          <Badge className="bg-red-100 text-red-700 text-xs">Zorunlu</Badge>
                        )}
                      </div>
                      <p className="font-medium text-navy">{question.text}</p>
                      {question.options && question.options.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {question.options.map((opt, i) => (
                            <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                              {opt}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleOpenFormQuestionModal(question)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDeleteFormQuestion(question.question_id, question.text)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseFormQuestionsModal} className="rounded-xl">
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Form Question Add/Edit Modal */}
      <Dialog open={showAddFormQuestionModal} onOpenChange={setShowAddFormQuestionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileQuestion className="w-5 h-5" />
              {editingFormQuestion ? t("surveys:editQuestion") : t("settings:addNewQuestion")}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>{t("surveys:questionText")}</Label>
              <Input
                value={formQuestionForm.text}
                onChange={(e) => setFormQuestionForm({ ...formQuestionForm, text: e.target.value })}
                placeholder="Soru metnini girin..."
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label>{t("surveys:questionType")}</Label>
              <select
                value={formQuestionForm.type}
                onChange={(e) => setFormQuestionForm({ ...formQuestionForm, type: e.target.value, options: [] })}
                className="w-full mt-1 p-2 border rounded-xl bg-white"
              >
                {QUESTION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            
            {(formQuestionForm.type === "select" || formQuestionForm.type === "multiselect" || formQuestionForm.type === "multiple_choice") && (
              <div>
                <Label>{t("common:options")}</Label>
                <div className="space-y-2 mt-2">
                  {formQuestionForm.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="flex-1 bg-slate-50 px-3 py-2 rounded-xl text-sm">{option}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500"
                        onClick={() => handleRemoveFormOption(option)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      value={newFormOption}
                      onChange={(e) => setNewFormOption(e.target.value)}
                      placeholder={t("common:newOption")}
                      className="rounded-xl flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFormOption())}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddFormOption}
                      className="rounded-xl"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
              <input
                type="checkbox"
                id="form_is_required"
                checked={formQuestionForm.is_required}
                onChange={(e) => setFormQuestionForm({ ...formQuestionForm, is_required: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="form_is_required" className="cursor-pointer">
                Bu soru zorunlu
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseFormQuestionModal}
              className="rounded-xl"
            >
              İptal
            </Button>
            <Button
              onClick={handleSaveFormQuestion}
              disabled={!formQuestionForm.text.trim()}
              className="bg-navy hover:bg-navy/90 rounded-xl"
            >
              {editingFormQuestion ? (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Güncelle
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Ekle
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              {t('admin:deleteConfirmation')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <span className="font-semibold text-navy">&quot;{deleteTarget?.name || 'Bu öğe'}&quot;</span> silmek istediğinize emin misiniz?
                <br />
                <span className="text-red-500 text-sm">{t('common:actionCannotBeUndone')}</span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create New Plan Modal */}
      <Dialog open={showCreatePlanModal} onOpenChange={setShowCreatePlanModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5" />
              Yeni Plan Oluştur
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>{t("admin:planNameRequired")}</Label>
              <Input
                value={newPlanForm.name}
                onChange={(e) => setNewPlanForm({ ...newPlanForm, name: e.target.value })}
                placeholder={t("common:ph_ornBaslangicPremiumEnterprise")}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label>{t('common:description')}</Label>
              <Input
                value={newPlanForm.description}
                onChange={(e) => setNewPlanForm({ ...newPlanForm, description: e.target.value })}
                placeholder={t("admin:ph_planAciklamasi")}
                className="rounded-xl mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("common:monthlyPriceTL")}</Label>
                <Input
                  type="number"
                  value={newPlanForm.monthly_price}
                  onChange={(e) => setNewPlanForm({ ...newPlanForm, monthly_price: parseInt(e.target.value) || 0 })}
                  className="rounded-xl mt-1"
                />
              </div>
              <div>
                <Label>{t("common:yearlyPriceTL")}</Label>
                <Input
                  type="number"
                  value={newPlanForm.yearly_price}
                  onChange={(e) => setNewPlanForm({ ...newPlanForm, yearly_price: parseInt(e.target.value) || 0 })}
                  className="rounded-xl mt-1"
                />
              </div>
            </div>
            <div>
              <Label>{t("common:memberLimitUnlimited")}</Label>
              <Input
                type="number"
                value={newPlanForm.limits?.members ?? -1}
                onChange={(e) => setNewPlanForm({ ...newPlanForm, limits: { ...newPlanForm.limits, members: parseInt(e.target.value) || 0 } })}
                className="rounded-xl mt-1"
              />
            </div>
            
            {/* Module Selection in Create Modal */}
            <div>
              <Label className="mb-2 block">{t("admin:aktifModuller")}</Label>
              <div className="border rounded-xl p-3 max-h-48 overflow-y-auto space-y-2">
                {Object.entries(MODULE_GROUPS).map(([groupId, groupInfo]) => {
                  const groupModules = systemModules.filter(m => m.group === groupId);
                  if (groupModules.length === 0) return null;
                  return (
                    <div key={groupId} className="space-y-1">
                      <p className="text-xs font-medium text-grey">{groupInfo.label}</p>
                      <div className="flex flex-wrap gap-1">
                        {groupModules.map(mod => (
                          <label 
                            key={mod.id} 
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs cursor-pointer transition-colors ${
                              newPlanForm.allowed_modules?.includes(mod.id)
                                ? groupInfo.color
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={newPlanForm.allowed_modules?.includes(mod.id) || false}
                              onChange={(e) => {
                                const currentModules = newPlanForm.allowed_modules || [];
                                const newModules = e.target.checked
                                  ? [...currentModules, mod.id]
                                  : currentModules.filter(m => m !== mod.id);
                                setNewPlanForm({ ...newPlanForm, allowed_modules: newModules });
                              }}
                              className="sr-only"
                            />
                            {mod.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-grey mt-1">
                Seçili: {newPlanForm.allowed_modules?.length || 0} modül
              </p>
            </div>
            
            <div className="p-3 bg-amber-50 rounded-xl">
              <p className="text-xs text-amber-700">
                <strong>Not:</strong> Plan oluşturulduktan sonra da modülleri düzenleyebilirsiniz. 
                Mevcut aboneler, abone oldukları tarihteki modül haklarını korur (grandfathering).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePlanModal(false)} className="rounded-xl">
              İptal
            </Button>
            <Button onClick={handleCreatePlan} disabled={saving || !newPlanForm.name.trim()} className="bg-navy hover:bg-navy/90 rounded-xl">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Modules Modal */}
      <Dialog open={showModulesModal} onOpenChange={setShowModulesModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              {selectedPlanForModules?.name} - Modül Yönetimi
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSelectAllModules(true)}
                className="rounded-xl"
              >
                Tümünü Seç
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSelectAllModules(false)}
                className="rounded-xl"
              >
                Tümünü Kaldır
              </Button>
              <Badge variant="secondary" className="ml-auto">
                {selectedPlanForModules?.allowed_modules?.length || 0} / {systemModules.length} modül seçili
              </Badge>
            </div>
            
            {/* Modules by Group */}
            <div className="space-y-4">
              {Object.entries(MODULE_GROUPS).map(([groupId, groupInfo]) => {
                const groupModules = systemModules.filter(m => m.group === groupId);
                if (groupModules.length === 0) return null;
                
                const selectedCount = groupModules.filter(m => 
                  selectedPlanForModules?.allowed_modules?.includes(m.id)
                ).length;
                
                return (
                  <div key={groupId} className="border rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-navy flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-full ${groupInfo.color.split(" ")[0]}`} />
                        {groupInfo.label}
                      </h4>
                      <Badge variant="secondary" className="text-xs">
                        {selectedCount} / {groupModules.length}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {groupModules.map(mod => {
                        const isSelected = selectedPlanForModules?.allowed_modules?.includes(mod.id);
                        return (
                          <label
                            key={mod.id}
                            className={`flex items-center gap-2 p-2 rounded-xl cursor-pointer transition-colors ${
                              isSelected
                                ? groupInfo.color
                                : "bg-gray-50 hover:bg-gray-100"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleModule(mod.id)}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm">{mod.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="p-3 bg-blue-50 rounded-xl">
              <p className="text-xs text-blue-700">
                <strong>Grandfathering:</strong> Bu değişiklikler sadece yeni aboneleri etkiler. 
                Mevcut aboneler, abone oldukları tarihteki modül haklarını korumaya devam eder.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModulesModal(false)} className="rounded-xl">
              İptal
            </Button>
            <Button onClick={handleSavePlanModules} disabled={saving} className="bg-navy hover:bg-navy/90 rounded-xl">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {t("admin:saveModules")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Features Modal */}
      <Dialog open={showFeaturesModal} onOpenChange={setShowFeaturesModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              {selectedPlanForFeatures?.name} - {t("common:featureManagement")}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Language selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">{t("common:translationLang")}:</span>
              <select
                value={selectedTransLang}
                onChange={(e) => setSelectedTransLang(e.target.value)}
                className="h-8 px-2 text-sm border rounded-lg bg-white"
              >
                {availableLanguages.filter(l => l !== "tr").map(lang => (
                  <option key={lang} value={lang}>{lang.toUpperCase()}</option>
                ))}
              </select>
            </div>

            {/* Add new feature */}
            <div className="flex gap-2">
              <Input
                value={newFeatureText}
                onChange={(e) => setNewFeatureText(e.target.value)}
                placeholder={t("common:addNewFeature")}
                className="rounded-xl flex-1"
                onKeyDown={(e) => e.key === "Enter" && handleAddFeature()}
              />
              <Button onClick={handleAddFeature} disabled={!newFeatureText.trim()} className="bg-navy hover:bg-navy/90 rounded-xl">
                <Plus className="w-4 h-4 mr-1" />
                {t("common:add")}
              </Button>
            </div>

            {/* Features list */}
            <div className="space-y-2">
              {selectedPlanForFeatures?.display_features?.length > 0 ? (
                selectedPlanForFeatures.display_features.map((feature, index) => (
                  <div 
                    key={index} 
                    className={`flex items-center gap-3 p-3 rounded-xl border ${
                      feature.visible ? "bg-white" : "bg-gray-50 opacity-60"
                    }`}
                  >
                    {/* Include/Exclude toggle */}
                    <button
                      onClick={() => handleToggleFeatureIncluded(index)}
                      className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center transition-colors ${
                        feature.included 
                          ? "bg-green-100 text-green-600 hover:bg-green-200" 
                          : "bg-red-100 text-red-600 hover:bg-red-200"
                      }`}
                      title={feature.included ? t("common:included") : t("common:notIncluded")}
                    >
                      {feature.included ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                    </button>
                    
                    {/* Feature name TR + Translation */}
                    <div className="flex-1 flex flex-col gap-1">
                      <Input
                        className="h-8 text-sm rounded-lg"
                        value={feature.name}
                        onChange={(e) => {
                          const features = [...selectedPlanForFeatures.display_features];
                          features[index] = { ...features[index], name: e.target.value };
                          setSelectedPlanForFeatures({ ...selectedPlanForFeatures, display_features: features });
                        }}
                        placeholder="TR"
                      />
                      <Input
                        className="h-8 text-sm rounded-lg text-slate-500"
                        value={feature.translations?.[selectedTransLang] || ""}
                        onChange={(e) => {
                          const features = [...selectedPlanForFeatures.display_features];
                          if (!features[index].translations) features[index].translations = {};
                          features[index] = { ...features[index], translations: { ...features[index].translations, [selectedTransLang]: e.target.value } };
                          setSelectedPlanForFeatures({ ...selectedPlanForFeatures, display_features: features });
                        }}
                        placeholder={`${selectedTransLang.toUpperCase()} ${t("common:featureTranslation").toLowerCase()}`}
                      />
                    </div>
                    
                    {/* Visibility toggle */}
                    <button
                      onClick={() => handleToggleFeatureVisible(index)}
                      className={`px-2 py-1 text-xs rounded-lg transition-colors flex-shrink-0 ${
                        feature.visible 
                          ? "bg-blue-100 text-blue-600 hover:bg-blue-200" 
                          : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                      }`}
                      title={feature.visible ? t("common:visible") : t("common:hidden")}
                    >
                      {feature.visible ? t("common:visible") : t("common:hidden")}
                    </button>
                    
                    {/* Delete */}
                    <button
                      onClick={() => handleDeleteFeature(index)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded flex-shrink-0"
                      title={t('common:delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-grey">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>{t("common:noFeaturesYet")}</p>
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="p-3 bg-gray-50 rounded-xl space-y-2">
              <p className="text-sm font-medium text-navy">{t("common:legend")}:</p>
              <div className="flex flex-wrap gap-4 text-xs text-grey">
                <div className="flex items-center gap-1">
                  <div className="w-5 h-5 bg-green-100 rounded flex items-center justify-center">
                    <Check className="w-3 h-3 text-green-600" />
                  </div>
                  <span>= {t("common:included")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-5 h-5 bg-red-100 rounded flex items-center justify-center">
                    <X className="w-3 h-3 text-red-600" />
                  </div>
                  <span>= {t("common:notIncluded")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="px-1 bg-blue-100 text-blue-600 rounded">{t("common:visible")}</span>
                  <span>= {t("common:showOnPricing")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="px-1 bg-gray-200 text-gray-600 rounded">{t("common:hidden")}</span>
                  <span>= {t("common:hideOnPricing")}</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeaturesModal(false)} className="rounded-xl">
              {t("common:cancel")}
            </Button>
            <Button onClick={handleSavePlanFeatures} disabled={saving} className="bg-navy hover:bg-navy/90 rounded-xl">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {t("common:saveFeatures")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Plan Confirmation Dialog */}
      <AlertDialog open={deletePlanDialog.open} onOpenChange={(open) => !deletingPlan && setDeletePlanDialog({ open, planId: open ? deletePlanDialog.planId : null, planName: open ? deletePlanDialog.planName : "" })}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common:deletePlan')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("common:deletePlanConfirm", { name: deletePlanDialog.planName })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" disabled={deletingPlan}>{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeletePlan}
              disabled={deletingPlan}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
            >
              {deletingPlan ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {deletingPlan ? t("common:deleting") : t("common:yesDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
